import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { quoteAPI, conversationAPI } from '../../services/api';

const CHANNEL_LABELS = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  sms: 'SMS'
};

const ConversationTimeline = ({ quoteRequestId }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const response = await conversationAPI.getForQuoteRequest(quoteRequestId);
        setConversations(response.data || []);
      } catch (err) {
        console.error('Error fetching conversations:', err);
      } finally {
        setLoading(false);
      }
    };

    if (quoteRequestId) {
      fetchConversations();
    }
  }, [quoteRequestId]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (loading) {
    return <p style={{ color: '#666', fontStyle: 'italic' }}>Loading conversations...</p>;
  }

  if (conversations.length === 0) {
    return <p style={{ color: '#666' }}>No conversations recorded yet.</p>;
  }

  return (
    <div className="conversation-timeline">
      {conversations.map((conv) => {
        const isExpanded = expandedIds.has(conv.id);
        const isOutbound = conv.direction === 'outbound';
        const timestamp = conv.message_timestamp || conv.messageTimestamp || conv.created_at;

        return (
          <div
            key={conv.id}
            className="conversation-item"
            style={{
              borderLeft: `3px solid ${isOutbound ? '#3b82f6' : '#10b981'}`,
              padding: '8px 12px',
              marginBottom: '8px',
              backgroundColor: '#f9fafb',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onClick={() => toggleExpand(conv.id)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: isOutbound ? '#3b82f6' : '#10b981'
                  }}
                >
                  {isOutbound ? 'SENT' : 'RECEIVED'}
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#374151',
                    backgroundColor: '#e5e7eb'
                  }}
                >
                  {CHANNEL_LABELS[conv.channel] || conv.channel}
                </span>
                {conv.provider && (
                  <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                    {conv.provider.name}
                  </span>
                )}
              </div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                {timestamp ? new Date(timestamp).toLocaleString() : ''}
              </span>
            </div>

            {conv.subject && (
              <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                <strong>Subject:</strong> {conv.subject}
              </div>
            )}

            {isExpanded && conv.body && (
              <pre style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '3px',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                {conv.body}
              </pre>
            )}

            {isExpanded && conv.attachments && conv.attachments.length > 0 && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#6b7280' }}>
                Attachments: {conv.attachments.map(a => a.filename || 'unnamed').join(', ')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const EvidenceViewer = ({ quote, onClose }) => {
  const [evidenceUrl, setEvidenceUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('evidence');

  useEffect(() => {
    const fetchEvidence = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await quoteAPI.getEvidence(quote.id);
        setEvidenceUrl(response.data.url);
      } catch (err) {
        console.error('Error fetching evidence:', err);
        setError(err.response?.data?.error || 'Failed to load evidence');
      } finally {
        setLoading(false);
      }
    };

    if (quote?.id) {
      fetchEvidence();
    }
  }, [quote?.id]);

  const handleDownload = () => {
    if (evidenceUrl) {
      window.open(evidenceUrl, '_blank');
    }
  };

  const isImage = quote?.sourceDocumentType?.startsWith('image/');
  const isPDF = quote?.sourceDocumentType === 'application/pdf';
  const isText = quote?.sourceDocumentType === 'text/plain';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Quote Evidence - {quote?.provider?.name || 'Provider'}</h2>
          <button onClick={onClose} className="btn-close" aria-label="Close">
            &times;
          </button>
        </div>

        {/* Tab navigation */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 16px' }}>
          <button
            onClick={() => setActiveTab('evidence')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderBottom: activeTab === 'evidence' ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'evidence' ? 600 : 400,
              color: activeTab === 'evidence' ? '#3b82f6' : '#6b7280'
            }}
          >
            Evidence
          </button>
          <button
            onClick={() => setActiveTab('conversations')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderBottom: activeTab === 'conversations' ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === 'conversations' ? 600 : 400,
              color: activeTab === 'conversations' ? '#3b82f6' : '#6b7280'
            }}
          >
            Conversations
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'evidence' && (
            <>
              {loading && (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading evidence...</p>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <p>Error: {error}</p>
                </div>
              )}

              {!loading && !error && evidenceUrl && (
                <div className="evidence-content">
                  {isImage && (
                    <div className="evidence-image-container">
                      <img src={evidenceUrl} alt="Quote evidence" className="evidence-image" />
                    </div>
                  )}

                  {isPDF && (
                    <div className="evidence-pdf-container">
                      <iframe
                        src={evidenceUrl}
                        title="Quote evidence PDF"
                        className="evidence-pdf"
                      />
                    </div>
                  )}

                  {isText && (
                    <div className="evidence-text-container">
                      <iframe
                        src={evidenceUrl}
                        title="Quote evidence text"
                        className="evidence-text"
                      />
                    </div>
                  )}

                  {!isImage && !isPDF && !isText && (
                    <div className="evidence-unsupported">
                      <p>Preview not available for this file type.</p>
                      <p>File type: {quote.sourceDocumentType}</p>
                      <button onClick={handleDownload} className="btn-primary">
                        Download File
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="evidence-context">
                <h3>Quote Context</h3>
                <div className="context-grid">
                  <div className="context-item">
                    <label>Provider</label>
                    <span>{quote?.provider?.name || 'N/A'}</span>
                  </div>
                  <div className="context-item">
                    <label>Total Cost</label>
                    <strong>${quote?.totalCost?.toLocaleString() || 'N/A'}</strong>
                  </div>
                  <div className="context-item">
                    <label>Source</label>
                    <span>{quote?.source || 'N/A'}</span>
                  </div>
                  <div className="context-item">
                    <label>Received At</label>
                    <span>
                      {quote?.receivedAt
                        ? new Date(quote.receivedAt).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {quote?.rawEmailText && (
                  <div className="raw-email">
                    <h4>Original Email Text</h4>
                    <pre className="email-text">{quote.rawEmailText}</pre>
                  </div>
                )}

                {quote?.notes && (
                  <div className="quote-notes">
                    <h4>Notes</h4>
                    <p>{quote.notes}</p>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'conversations' && (
            <div style={{ padding: '8px 0' }}>
              <h3 style={{ marginBottom: '12px' }}>Communication Timeline</h3>
              <ConversationTimeline quoteRequestId={quote?.quoteRequestId || quote?.quoteRequest?.id} />
            </div>
          )}
        </div>

        <div className="modal-footer">
          {activeTab === 'evidence' && (
            <button onClick={handleDownload} className="btn-secondary" disabled={!evidenceUrl}>
              Download Original
            </button>
          )}
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

EvidenceViewer.propTypes = {
  quote: PropTypes.shape({
    id: PropTypes.string.isRequired,
    quoteRequestId: PropTypes.string,
    quoteRequest: PropTypes.shape({
      id: PropTypes.string
    }),
    provider: PropTypes.shape({
      name: PropTypes.string
    }),
    totalCost: PropTypes.number,
    source: PropTypes.string,
    sourceDocumentType: PropTypes.string,
    receivedAt: PropTypes.string,
    rawEmailText: PropTypes.string,
    notes: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default EvidenceViewer;
